import React, { useEffect, useState } from 'react';
import { Avatar, SxProps } from '@mui/material';

import UserAvatarInitials from './UserAvatarInitials';
import { useStyle } from './styles';

/**
 * Shows the user's picture once it has loaded, and their initials until then
 * or whenever there is no picture to show.
 *
 * @param props.name - Name the initials placeholder is built from.
 * @param props.initials - Overrides the initials derived from the name.
 * @param props.src - Picture to show; the initials stand in while it loads.
 * @param props.onClick - Called on a click on either layer.
 * @param props.sx - Extra styles, merged into both layers.
 */
const UserAvatar = ({
  name,
  initials,
  src,
  onClick,
  sx = {},
}: {
  name: string;
  initials?: string;
  src?: string;
  onClick?: () => void;
  sx?: SxProps;
}) => {
  const classes = useStyle();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Both flags describe the picture in `src`, so a new one starts over.
    // Without the reset, a component that mounted without a picture stays
    // marked failed for good and never shows the one it is handed later —
    // which is every first upload in the profile editor.
    setImageLoaded(false);
    setImageError(!src);

    if (!src) {
      return;
    }

    const img = new Image();
    img.src = src;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return (
    <>
      <UserAvatarInitials
        name={name}
        initials={initials}
        sx={{ ...classes.avatar(!imageLoaded), ...sx }}
        onClick={onClick}
      />

      {src && !imageError && (
        <Avatar
          src={src}
          onClick={onClick}
          sx={{ ...classes.avatar(imageLoaded), ...sx }}
          onError={() => setImageError(true)}
        />
      )}
    </>
  );
};

export default UserAvatar;
